import { EMPTY_STRING } from '../../../NonReact/constants/Common'
import { IsVarEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { messageType } from '../Types/Types'

export const ERROR_DEDUPE_TTL_MS: number = 15_000

/**
 * Utility class for handling message display logic, such as message emptiness checks
 * and suppressing duplicate error messages for a configurable time-to-live period.
 */
export class MessageDisplayUtils {
    private static readonly recentErrorKeys: Map<string, number> = new Map<string, number>()

    /**
     * Checks if the message is empty or consists only of whitespace.
     * @param message - The message to check.
     * @returns True if the message is empty or consists only of whitespace, otherwise false.
     */
    static isEmptyMessage(message: messageType): boolean {
        if (IsVarEmpty(message)) return true
        if (typeof message === 'string') return message.trim() === EMPTY_STRING
        return false
    }

    /**
     * Normalizes the error deduplication key by trimming whitespace and replacing multiple spaces with a single space.
     * @param message - The message to normalize.
     * @returns The normalized message.
     */
    private static normalizeErrorDedupeKey(message: string): string {
        return message.trim().replace(/\s+/g, ' ')
    }

    /**
     * Prunes expired error keys from the recent error keys map.
     * @param now - The current time.
     */
    private static pruneExpiredErrorKeys(now: number): void {
        MessageDisplayUtils.recentErrorKeys.forEach((at: number, key: string): void => {
            if (now - at >= ERROR_DEDUPE_TTL_MS) MessageDisplayUtils.recentErrorKeys.delete(key)
        })
    }

    /**
     * Checks if the message should be suppressed as a duplicate error message.
     * @param message - The message to check.
     * @returns True if the message should be suppressed, otherwise false.
     */
    static shouldSuppressDuplicateStringError(message: messageType): boolean {
        if (typeof message !== 'string') return false

        const key: string = MessageDisplayUtils.normalizeErrorDedupeKey(message)
        if (key === EMPTY_STRING) return false

        const now: number = Date.now()
        MessageDisplayUtils.pruneExpiredErrorKeys(now)

        const lastShown: Definable<number> = MessageDisplayUtils.recentErrorKeys.get(key)
        if (isVarNotEmpty(lastShown) && now - lastShown < ERROR_DEDUPE_TTL_MS) return true

        MessageDisplayUtils.recentErrorKeys.set(key, now)
        return false
    }
}
